import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertTriangle, Save, Send, Plus, Trash2, ImageIcon, MessageSquare } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Skeleton } from '../../components/ui/Skeleton';
import { useCreatePlanilla, usePlanillaDetail, useUpsertPlanillaDetails, useSubmitPlanilla, useCreateDescuento, useDeleteDescuento, useCreateExtra, useDeleteExtra, usePlanillaProgress, useReturnToResidente, useSendToGerente, useApprovePlanilla, useRejectPlanilla, useCreateComment } from '../../hooks/usePlanillas';
import { Modal } from '../../components/ui/Modal';
import { useAuthStore } from '../../stores/auth-store';
import type { PlanillaMaestroDTO, PlanillaDetailItemDTO, DescuentoDTO, ExtraDTO } from '../../hooks/usePlanillas';
import { useCreatePlanillaFoto } from '../../hooks/usePlanillaFotos';
import { FileUpload, type UploadedFile } from '../../components/shared/FileUpload';
import { toast } from '../../components/ui/Toast';
import { useRecipes, useRecipe } from '../../hooks/useRecipes';
import type { RecipeListDTO } from '../../hooks/useRecipes';
import { useCabeceras } from '../../hooks/useCabeceras';
import type { CabeceraDTO } from '../../hooks/useCabeceras';

// Runtime extension: recipe-service returns is_mdo and material_precio_unitario but not in the TS type
interface RecipeMaterialWithMdo {
  material_id: string;
  material_nombre: string;
  is_mdo?: boolean;
  material_precio_unitario?: number;
}

// ── Types ───────────────────────────────────────────────────────────

interface DetailDraft {
  id?: string | null;
  maestroId: string;
  recipeId: string;
  mdoMaterialId: string | null;
  cabeceraId?: string | null;
  recipeName: string;
  mdoMaterialNombre: string | null;
  unidad: string;
  precioUnitario: number;
  cantidad: number;
  notas: string;
}

// ── Main Page ───────────────────────────────────────────────────────

export function NuevaPlanillaPage() {
  const { id: projectId, planillaId } = useParams<{ id: string; planillaId?: string }>();
  const navigate = useNavigate();
  // Create flow state
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [comentarios, setComentarios] = useState('');
  const [createdPlanillaId, setCreatedPlanillaId] = useState<string | null>(planillaId ?? null);

  const createPlanilla = useCreatePlanilla(projectId!);

  // Load detail if editing or after creation
  const { data: planilla, isLoading: loadingDetail, refetch: refetchPlanilla } = usePlanillaDetail(createdPlanillaId ?? '');
  const upsertDetails = useUpsertPlanillaDetails(createdPlanillaId ?? '');
  const submitPlanilla = useSubmitPlanilla(createdPlanillaId ?? '');

  // Transition hooks for review/approval actions
  const { user } = useAuthStore();
  const userRole = user?.role ?? '';
  const returnToResidente = useReturnToResidente(createdPlanillaId ?? '');
  const sendToGerente = useSendToGerente(createdPlanillaId ?? '');
  const approvePlanilla = useApprovePlanilla(createdPlanillaId ?? '');
  const rejectPlanilla = useRejectPlanilla(createdPlanillaId ?? '');
  const createComment = useCreateComment(createdPlanillaId ?? '');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [returnMotivo, setReturnMotivo] = useState('');
  const [rejectMotivo, setRejectMotivo] = useState('');
  const [newComment, setNewComment] = useState('');
  // Admin descuento state (pendiente_administrativo only)
  const [showAdminDescuento, setShowAdminDescuento] = useState(false);
  const [adminDescuentoDesc, setAdminDescuentoDesc] = useState('');
  const [adminDescuentoMonto, setAdminDescuentoMonto] = useState('');
  const [adminDescuentoMaestroId, setAdminDescuentoMaestroId] = useState('');
  const createDescuentoAdmin = useCreateDescuento(createdPlanillaId ?? '');

  // Cabeceras (housing unit headers) for this project
  const { data: cabeceras = [], isLoading: cabecerasLoading } = useCabeceras(projectId!);
  const hasCabeceras = cabeceras.length > 0;

  // Progress data for inline context
  // Key: `${recipe_id}:${mdo_material_id ?? ''}` — ensures per-MDO tracking
  const { data: progressData } = usePlanillaProgress(projectId!);
  const progressMap = useMemo(() => {
    const map = new Map<string, { cantidad_ot: number; cantidad_registrada: number; unidad: string }>();
    if (progressData) {
      for (const item of progressData) {
        const key = `${item.recipe_id}:${item.mdo_material_id ?? ''}`;
        map.set(key, { cantidad_ot: item.cantidad_ot, cantidad_registrada: item.cantidad_registrada, unidad: item.unidad });
      }
    }
    return map;
  }, [progressData]);

  // Photo upload
  const createFoto = useCreatePlanillaFoto(createdPlanillaId ?? '');

  const registeredFotoKeys = useRef(new Set<string>());

  const handleFilesChange = (files: UploadedFile[]) => {
    if (!createdPlanillaId || !planilla) return;
    const existingKeys = new Set(planilla.fotos.map((f) => f.s3_key));
    for (const file of files) {
      if (!existingKeys.has(file.s3_key) && !registeredFotoKeys.current.has(file.s3_key)) {
        registeredFotoKeys.current.add(file.s3_key);
        createFoto.mutate({
          s3Key: file.s3_key,
          filename: file.filename,
          mimeType: file.mime_type,
          sizeBytes: file.size_bytes,
        });
      }
    }
  };

  // Details draft state (per maestro per recipe → cantidad)
  const [detailsDraft, setDetailsDraft] = useState<Map<string, DetailDraft>>(new Map());
  // Selected keys: only selected rows will show cantidad inputs and be submitted
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Initialize draft once: saved details first (with IDs), then any unsaved rows.
  // Key format: `${maestroId}:${recipeId}:${mdoMaterialId ?? ''}:${cabeceraId ?? ''}`.
  // Wait for cabecerasLoading so expansion happens with the full cabecera list.
  useEffect(() => {
    if (planilla && !initialized && !cabecerasLoading) {
      const draft = new Map<string, DetailDraft>();
      const selected = new Set<string>();
      const hasCab = cabeceras.length > 0;

      // 1. Populate from saved details (carry IDs so subsequent saves use UPDATE)
      for (const d of planilla.details) {
        const key = `${d.maestro_id}:${d.recipe_id}:${d.mdo_material_id ?? ''}:${d.cabecera_id ?? ''}`;
        draft.set(key, {
          id: d.id,
          maestroId: d.maestro_id,
          recipeId: d.recipe_id,
          mdoMaterialId: d.mdo_material_id,
          cabeceraId: d.cabecera_id ?? null,
          recipeName: d.recipe_nombre,
          mdoMaterialNombre: d.mdo_material_nombre,
          unidad: d.unidad,
          precioUnitario: d.precio_unitario,
          cantidad: d.cantidad,
          notas: d.notas ?? '',
        });
        if (d.cantidad > 0) selected.add(key);
      }

      // 2. Add unsaved rows from maestro rates not already in draft.
      // When project has cabeceras: don't pre-create rows — the residente adds
      // cabecera entries manually via the "+" button. Saved entries from step 1 are
      // already loaded. When no cabeceras: single row per APU (original behavior).
      for (const m of planilla.maestros) {
        for (const r of m.rates) {
          if (r.mdo_items && r.mdo_items.length > 0) {
            for (const mdo of r.mdo_items) {
              if (!hasCab) {
                const k = `${m.maestro_id}:${r.recipe_id}:${mdo.material_id}:`;
                if (!draft.has(k)) draft.set(k, {
                  maestroId: m.maestro_id,
                  recipeId: r.recipe_id,
                  mdoMaterialId: mdo.material_id,
                  mdoMaterialNombre: mdo.material_nombre,
                  recipeName: r.recipe_nombre,
                  unidad: r.unidad,
                  precioUnitario: mdo.precio_unitario,
                  cantidad: 0,
                  notas: '',
                  cabeceraId: null,
                });
              }
              // If hasCab: entries come from saved details only; user adds via "+" button
            }
          } else {
            if (!hasCab) {
              const k = `${m.maestro_id}:${r.recipe_id}::`;
              if (!draft.has(k)) draft.set(k, {
                maestroId: m.maestro_id,
                recipeId: r.recipe_id,
                mdoMaterialId: null,
                mdoMaterialNombre: null,
                recipeName: r.recipe_nombre,
                unidad: r.unidad,
                precioUnitario: r.precio_unitario,
                cantidad: 0,
                notas: '',
                cabeceraId: null,
              });
            }
            // If hasCab: entries come from saved details only; user adds via "+" button
          }
        }
      }

      setDetailsDraft(draft);
      setSelectedKeys(selected);
      setInitialized(true);
    }
  }, [planilla, initialized, cabecerasLoading, cabeceras]);

  // Step 1: Create planilla
  const handleCreate = async () => {
    if (!fecha) return;
    try {
      const result = await createPlanilla.mutateAsync({
        fecha,
        comentarios: comentarios.trim() || null,
      });
      setCreatedPlanillaId(result.id);
      toast('success', `Planilla N°${result.numero} creada`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast('error', msg || 'Error al crear planilla');
    }
  };

  // Step 2: Save details
  const handleSaveDetails = async () => {
    const items = Array.from(detailsDraft.entries())
      .filter(([key, d]) => {
        // With cabeceras: all entries are explicit assignments — include them (even 0)
        if (hasCabeceras) return true;
        // Without cabeceras: non-MDO rows must have quantity > 0 (rows exist for all APUs by default)
        if (d.mdoMaterialId === null) return d.cantidad > 0;
        // MDO sub-items: include if explicitly selected by checkbox (allow 0)
        return selectedKeys.has(key);
      })
      .map(([, d]) => d)
      .map((d) => ({
        id: d.id ?? undefined,
        maestroId: d.maestroId,
        recipeId: d.recipeId,
        mdoMaterialId: d.mdoMaterialId ?? null,
        cabeceraId: d.cabeceraId ?? undefined,
        cantidad: d.cantidad,
        precioUnitario: d.precioUnitario,
        notas: d.notas || null,
      }));

    try {
      await upsertDetails.mutateAsync(items);
      const result = await refetchPlanilla();
      // Rebuild draft from server data so IDs, cabeceras, and quantities are
      // always in sync with what was persisted (prevents stale entries from hiding).
      if (result.data?.details) {
        const freshDraft = new Map<string, DetailDraft>();
        const freshSelected = new Set<string>();
        for (const d of result.data.details) {
          const key = `${d.maestro_id}:${d.recipe_id}:${d.mdo_material_id ?? ''}:${d.cabecera_id ?? ''}`;
          freshDraft.set(key, {
            id: d.id,
            maestroId: d.maestro_id,
            recipeId: d.recipe_id,
            mdoMaterialId: d.mdo_material_id,
            cabeceraId: d.cabecera_id ?? null,
            recipeName: d.recipe_nombre,
            mdoMaterialNombre: d.mdo_material_nombre,
            unidad: d.unidad,
            precioUnitario: d.precio_unitario,
            cantidad: d.cantidad,
            notas: d.notas ?? '',
          });
          if (d.cantidad > 0) freshSelected.add(key);
        }
        setDetailsDraft(freshDraft);
        setSelectedKeys(freshSelected);
      }
      toast('success', 'Detalles guardados. Ahora puede enviar para revisión.');
    } catch {
      toast('error', 'Error al guardar detalles');
    }
  };

  // Add adicional rows (recipes not in maestro rates) to draft
  const handleAddAdicionales = (rows: DetailDraft[]) => {
    setDetailsDraft((prev) => {
      const next = new Map(prev);
      for (const row of rows) {
        const key = `${row.maestroId}:${row.recipeId}:${row.mdoMaterialId ?? ''}:${row.cabeceraId ?? ''}`;
        if (!next.has(key)) next.set(key, row);
      }
      return next;
    });
  };

  // Delete adicional rows (by key list) from draft
  const handleDeleteAdicionales = (keys: string[]) => {
    setDetailsDraft((prev) => {
      const next = new Map(prev);
      for (const key of keys) next.delete(key);
      return next;
    });
  };

  // Add a cabecera-assigned entry to the draft (used in hasCabeceras mode)
  const handleAddCabeceraEntry = (cabeceraId: string | null, draftBase: Omit<DetailDraft, 'cabeceraId' | 'id'>) => {
    const fullKey = `${draftBase.maestroId}:${draftBase.recipeId}:${draftBase.mdoMaterialId ?? ''}:${cabeceraId ?? ''}`;
    setDetailsDraft((prev) => {
      if (prev.has(fullKey)) return prev;
      const next = new Map(prev);
      next.set(fullKey, { ...draftBase, cabeceraId: cabeceraId ?? null });
      return next;
    });
  };

  // Remove a specific cabecera entry from draft (used in hasCabeceras mode)
  const handleRemoveCabeceraEntry = (fullKey: string) => {
    setDetailsDraft((prev) => {
      const next = new Map(prev);
      next.delete(fullKey);
      return next;
    });
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.delete(fullKey);
      return next;
    });
  };

  // Submit planilla for review
  const handleSubmit = async () => {
    try {
      await submitPlanilla.mutateAsync();
      toast('success', `Planilla N°${planilla?.numero} enviada para revisión`);
      navigate(`/proyectos/${projectId}/planillas`);
    } catch {
      toast('error', 'Error al enviar planilla. Verifique que tenga detalles o servicios varios.');
    }
  };

  // Can submit: planilla is borrador or correccion_solicitada, has details or extras
  const canSubmit = planilla
    && ['borrador', 'correccion_solicitada'].includes(planilla.estado)
    && (planilla.details.length > 0 || planilla.extras.length > 0);

  const updateDraft = (key: string, field: 'cantidad' | 'precioUnitario' | 'notas', value: number | string) => {
    setDetailsDraft((prev) => {
      const next = new Map(prev);
      const existing = next.get(key);
      if (existing) {
        next.set(key, { ...existing, [field]: value });
      }
      return next;
    });
  };

  // Calculate totals from draft + saved extras - saved descuentos
  const totalNeto = useMemo(() => {
    let sum = 0;
    for (const [key, d] of detailsDraft) {
      if (d.cantidad <= 0) continue;
      if (hasCabeceras) {
        sum += d.cantidad * d.precioUnitario;
      } else if (d.mdoMaterialId === null || selectedKeys.has(key)) {
        sum += d.cantidad * d.precioUnitario;
      }
    }
    // Add saved Servicios Varios (extras already persisted on server)
    for (const e of planilla?.extras ?? []) {
      sum += e.monto;
    }
    // Subtract saved descuentos
    for (const d of planilla?.descuentos ?? []) {
      sum -= d.monto;
    }
    return parseFloat(sum.toFixed(2));
  }, [detailsDraft, selectedKeys, planilla?.extras, planilla?.descuentos, hasCabeceras]);

  // ── Step 1: Date entry ──
  if (!createdPlanillaId) {
    return (
      <div className="space-y-6 fade-up max-w-lg mx-auto">
        <h1 className="text-xl font-bold text-brand-dark">Nueva Planilla</h1>

        <Card className="p-4 space-y-4">
          <Input
            label="Fecha de planilla"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            helperText="El sistema calcula automáticamente el período quincenal"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Comentarios</label>
            <textarea
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
              placeholder="Comentarios opcionales..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => navigate(`/proyectos/${projectId}`)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              isLoading={createPlanilla.isPending}
              disabled={!fecha}
            >
              Crear Planilla
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ── Step 2: Edit details ──
  if (loadingDetail) {
    return (
      <div className="space-y-4 fade-up">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!planilla) {
    return (
      <div className="p-4 text-center text-red-600">Error al cargar planilla</div>
    );
  }

  const maestros = planilla.maestros;

  return (
    <div className="space-y-4 fade-up pb-24">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-brand-dark">
            Planilla N°{planilla.numero}
          </h1>
          <p className="text-xs text-gray-500">
            {planilla.fecha_desde} al {planilla.fecha_hasta} · {planilla.project_nombre}
          </p>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium">
          {planilla.estado}
        </span>
      </div>

      {/* Progress indicator */}
      <PlanillaProgressPanel projectId={projectId!} />

      {/* Maestro sections */}
      {maestros.length === 0 ? (
        <Card className="p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">No hay maestros asignados a este proyecto</p>
          <p className="text-xs text-gray-400 mt-1">Asigne maestros desde el detalle del proyecto</p>
        </Card>
      ) : (
        maestros.map((m) => (
          <MaestroSection
            key={m.maestro_id}
            maestro={m}
            details={detailsDraft}
            existingDetails={planilla.details}
            descuentos={planilla.descuentos.filter((d) => d.maestro_id === m.maestro_id)}
            extras={planilla.extras.filter((e) => e.maestro_id === m.maestro_id)}
            planillaId={createdPlanillaId!}
            isEditable={
              ['borrador', 'correccion_solicitada'].includes(planilla.estado) ||
              (planilla.estado === 'pendiente_administrativo' && ['administrativo', 'gerente', 'operaciones'].includes(userRole)) ||
              (planilla.estado === 'revisada' && ['gerente', 'operaciones'].includes(userRole))
            }
            onUpdateDraft={updateDraft}
            progressMap={progressMap}
            selectedKeys={selectedKeys}
            onToggleKey={toggleKey}
            onAddAdicionales={handleAddAdicionales}
            onDeleteAdicionales={handleDeleteAdicionales}
            onAddCabeceraEntry={handleAddCabeceraEntry}
            onRemoveCabeceraEntry={handleRemoveCabeceraEntry}
            cabeceras={cabeceras}
            hasCabeceras={hasCabeceras}
          />
        ))
      )}

      {/* Photos section */}
      {['borrador', 'correccion_solicitada'].includes(planilla.estado) && (
        <Card padding="md">
          <FileUpload
            context="planilla_foto"
            onFilesChange={handleFilesChange}
            existingFiles={
              planilla.fotos.map((f) => ({
                s3_key: f.s3_key,
                filename: f.filename,
                mime_type: f.mime_type,
                size_bytes: 0,
              }))
            }
            maxFiles={20}
            label="Fotos de evidencia (opcional)"
            helpText="JPG, PNG, HEIC. Se comprime automáticamente si > 5MB."
            entityId={createdPlanillaId!}
          />
          {planilla.fotos.length > 0 && (
            <p className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <ImageIcon className="w-3.5 h-3.5" />
              {planilla.fotos.length} foto{planilla.fotos.length !== 1 ? 's' : ''} subida{planilla.fotos.length !== 1 ? 's' : ''}
            </p>
          )}
        </Card>
      )}

      {/* Descuento Administrativo — only for admin when pendiente_administrativo */}
      {planilla.estado === 'pendiente_administrativo' && ['administrativo', 'gerente', 'operaciones'].includes(userRole) && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700">Descuento Administrativo</h3>
            {!showAdminDescuento && (
              <Button size="sm" variant="secondary" onClick={() => setShowAdminDescuento(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Agregar
              </Button>
            )}
          </div>
          {showAdminDescuento && (
            <div className="space-y-3 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Maestro</label>
                <select
                  value={adminDescuentoMaestroId}
                  onChange={(e) => setAdminDescuentoMaestroId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                >
                  <option value="">Seleccionar maestro...</option>
                  {planilla.maestros.map((m) => (
                    <option key={m.maestro_id} value={m.maestro_id}>{m.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Descripción</label>
                <input
                  type="text"
                  value={adminDescuentoDesc}
                  onChange={(e) => setAdminDescuentoDesc(e.target.value)}
                  placeholder="Descripción del descuento..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Monto ($)</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={adminDescuentoMonto}
                  onChange={(e) => setAdminDescuentoMonto(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="secondary" onClick={() => {
                  setShowAdminDescuento(false);
                  setAdminDescuentoDesc('');
                  setAdminDescuentoMonto('');
                  setAdminDescuentoMaestroId('');
                }}>
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  isLoading={createDescuentoAdmin.isPending}
                  disabled={!adminDescuentoMaestroId || !adminDescuentoDesc.trim() || !adminDescuentoMonto || parseFloat(adminDescuentoMonto) <= 0}
                  onClick={async () => {
                    try {
                      await createDescuentoAdmin.mutateAsync({
                        tipo: 'administrativo',
                        descripcion: adminDescuentoDesc.trim(),
                        monto: parseFloat(adminDescuentoMonto),
                        maestroId: adminDescuentoMaestroId,
                      });
                      setAdminDescuentoDesc('');
                      setAdminDescuentoMonto('');
                      setAdminDescuentoMaestroId('');
                      setShowAdminDescuento(false);
                      refetchPlanilla();
                      toast('success', 'Descuento administrativo agregado');
                    } catch {
                      toast('error', 'Error al agregar descuento');
                    }
                  }}
                >
                  Guardar
                </Button>
              </div>
            </div>
          )}
          {!showAdminDescuento && planilla.descuentos.filter((d) => d.tipo === 'administrativo').length === 0 && (
            <p className="text-xs text-gray-400 italic">Sin descuentos administrativos registrados</p>
          )}
          {!showAdminDescuento && planilla.descuentos.filter((d) => d.tipo === 'administrativo').length > 0 && (
            <div className="space-y-2 pt-1">
              {planilla.descuentos.filter((d) => d.tipo === 'administrativo').map((d) => (
                <div key={d.id} className="flex justify-between text-sm">
                  <p className="text-gray-700">{d.descripcion}</p>
                  <span className="font-medium text-red-600">-${d.monto.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Comments */}
      {planilla && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Comentarios ({planilla.comments.length})
          </h3>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {planilla.comments.map((c) => (
              <div key={c.id} className="text-sm">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-gray-700">{c.created_by.nombre}</span>
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleString('es-EC')}</span>
                </div>
                <p className="text-gray-600 whitespace-pre-wrap">{c.mensaje}</p>
              </div>
            ))}
            {planilla.comments.length === 0 && (
              <p className="text-xs text-gray-400 italic">Sin comentarios</p>
            )}
          </div>
          <div className="flex gap-2 mt-3 pt-3 border-t">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Agregar comentario..."
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && newComment.trim()) {
                  try {
                    await createComment.mutateAsync(newComment.trim());
                    setNewComment('');
                    toast('success', 'Comentario agregado');
                  } catch {
                    toast('error', 'Error al agregar comentario');
                  }
                }
              }}
            />
            <Button
              size="sm"
              disabled={!newComment.trim()}
              isLoading={createComment.isPending}
              onClick={async () => {
                if (!newComment.trim()) return;
                try {
                  await createComment.mutateAsync(newComment.trim());
                  setNewComment('');
                  toast('success', 'Comentario agregado');
                } catch {
                  toast('error', 'Error al agregar comentario');
                }
              }}
            >
              Enviar
            </Button>
          </div>
        </Card>
      )}

      {/* Totals summary */}
      {planilla && planilla.totals && (
        <Card className="p-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Total rubros</span>
              <span className="font-medium">${planilla.totals.total_rubros.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Servicios Varios</span>
              <span className="font-medium text-green-700">+${planilla.totals.total_extras.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-red-600">
              <span>Total descuentos</span>
              <span className="font-medium">-${planilla.totals.total_descuentos.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between border-t pt-2 font-bold text-lg text-brand-dark">
              <span>Total neto</span>
              <span>${planilla.totals.total_neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Hint when planilla is ready to submit */}
      {canSubmit && (
        <Card className="p-3 bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-800 flex items-center gap-2">
            <Send className="w-4 h-4" />
            Detalles guardados. Presione <strong>Enviar para revisión</strong> para pasar a Administrativo.
          </p>
        </Card>
      )}

      {/* Review/Approval action buttons — based on state + role */}
      {planilla && planilla.estado === 'pendiente_administrativo' && ['administrativo', 'gerente', 'operaciones'].includes(userRole) && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-brand-dark mb-3">Acciones de Revisión</h3>
          <div className="flex gap-3">
            <Button variant="primary" onClick={async () => {
              try { await sendToGerente.mutateAsync(); toast('success', 'Planilla enviada a gerencia'); refetchPlanilla(); } catch { toast('error', 'Error al enviar'); }
            }} isLoading={sendToGerente.isPending}>
              Aprobar y enviar a Gerencia
            </Button>
            <Button variant="danger" onClick={() => setShowReturnModal(true)}>
              Devolver al residente
            </Button>
          </div>
        </Card>
      )}

      {planilla && planilla.estado === 'revisada' && ['gerente', 'operaciones'].includes(userRole) && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-brand-dark mb-3">Aprobación Final</h3>
          <div className="flex gap-3">
            <Button variant="primary" onClick={async () => {
              try { await approvePlanilla.mutateAsync(); toast('success', 'Planilla aprobada'); refetchPlanilla(); } catch { toast('error', 'Error al aprobar'); }
            }} isLoading={approvePlanilla.isPending}>
              Aprobar Planilla
            </Button>
            <Button variant="danger" onClick={() => setShowRejectModal(true)}>
              Rechazar
            </Button>
          </div>
        </Card>
      )}

      {/* Return to residente modal */}
      <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)} title="Devolver al Residente" size="sm">
        <div className="space-y-4">
          <textarea
            rows={3}
            placeholder="Motivo de la devolución"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={returnMotivo}
            onChange={(e) => setReturnMotivo(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowReturnModal(false)}>Cancelar</Button>
            <Button variant="danger" disabled={returnMotivo.trim().length === 0} onClick={async () => {
              try { await returnToResidente.mutateAsync(returnMotivo); toast('success', 'Planilla devuelta al residente'); setShowReturnModal(false); refetchPlanilla(); } catch { toast('error', 'Error'); }
            }} isLoading={returnToResidente.isPending}>
              Devolver
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal isOpen={showRejectModal} onClose={() => setShowRejectModal(false)} title="Rechazar Planilla" size="sm">
        <div className="space-y-4">
          <textarea
            rows={3}
            placeholder="Motivo del rechazo (mín. 20 caracteres)"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
            value={rejectMotivo}
            onChange={(e) => setRejectMotivo(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancelar</Button>
            <Button variant="danger" disabled={rejectMotivo.length < 20} onClick={async () => {
              try { await rejectPlanilla.mutateAsync(rejectMotivo); toast('success', 'Planilla rechazada'); setShowRejectModal(false); refetchPlanilla(); } catch { toast('error', 'Error'); }
            }} isLoading={rejectPlanilla.isPending}>
              Rechazar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Total neto</p>
            <p className="text-lg font-bold text-brand-dark">
              ${totalNeto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
          <div className="flex gap-2">
            {/* Show Guardar when residente editing OR when admin/gerente reviewing */}
            {(canSubmit || ['borrador', 'correccion_solicitada'].includes(planilla.estado) ||
              (planilla.estado === 'pendiente_administrativo' && ['administrativo', 'gerente', 'operaciones'].includes(userRole)) ||
              (planilla.estado === 'revisada' && ['gerente', 'operaciones'].includes(userRole))) && (
              <Button variant="secondary" onClick={handleSaveDetails} isLoading={upsertDetails.isPending}>
                <Save className="w-4 h-4 mr-1" />
                Guardar
              </Button>
            )}
            {/* Enviar para revisión only for residente in borrador/correccion */}
            {['borrador', 'correccion_solicitada'].includes(planilla.estado) && (
              <Button
                onClick={handleSubmit}
                isLoading={submitPlanilla.isPending}
                disabled={!canSubmit}
              >
                <Send className="w-4 h-4 mr-1" />
                Enviar para revisión
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Maestro Section ─────────────────────────────────────────────────

function MaestroSection({
  maestro,
  details,
  existingDetails,
  descuentos,
  extras,
  planillaId,
  isEditable,
  onUpdateDraft,
  progressMap,
  selectedKeys,
  onToggleKey,
  onAddAdicionales,
  onDeleteAdicionales,
  onAddCabeceraEntry,
  onRemoveCabeceraEntry,
  cabeceras,
  hasCabeceras,
}: {
  maestro: PlanillaMaestroDTO;
  details: Map<string, DetailDraft>;
  existingDetails: PlanillaDetailItemDTO[];
  descuentos: DescuentoDTO[];
  extras: ExtraDTO[];
  planillaId: string;
  isEditable: boolean;
  onUpdateDraft: (key: string, field: 'cantidad' | 'precioUnitario' | 'notas', value: number | string) => void;
  progressMap: Map<string, { cantidad_ot: number; cantidad_registrada: number; unidad: string }>;
  selectedKeys: Set<string>;
  onToggleKey: (key: string) => void;
  onAddAdicionales: (rows: DetailDraft[]) => void;
  onDeleteAdicionales: (keys: string[]) => void;
  onAddCabeceraEntry: (cabeceraId: string | null, draftBase: Omit<DetailDraft, 'cabeceraId' | 'id'>) => void;
  onRemoveCabeceraEntry: (fullKey: string) => void;
  cabeceras: CabeceraDTO[];
  hasCabeceras: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const [showMultaForm, setShowMultaForm] = useState(false);
  const [showServicioForm, setShowServicioForm] = useState(false);
  const [showServicioMaestroForm, setShowServicioMaestroForm] = useState(false);
  const [showAdicionalModal, setShowAdicionalModal] = useState(false);
  // Raw string values for cantidad inputs — lets user type "0" and see it while
  // the draft stores the parsed number 0 (avoids `0 || ''` showing blank).
  const [rawQuantities, setRawQuantities] = useState<Map<string, string>>(new Map());
  const setRawQty = (key: string, raw: string) =>
    setRawQuantities((prev) => { const next = new Map(prev); next.set(key, raw); return next; });
  // State for inline "add cabecera entry" form (hasCabeceras mode)
  const [addingForKey, setAddingForKey] = useState<string | null>(null);
  const [newCabeceraId, setNewCabeceraId] = useState<string>('');
  const [newCantidad, setNewCantidad] = useState<string>('');
  const [newPrecio, setNewPrecio] = useState<string>('');

  // Identify which recipe IDs are part of the maestro's regular rates
  const maestroRateRecipeIds = useMemo(
    () => new Set(maestro.rates.map((r) => r.recipe_id)),
    [maestro.rates],
  );

  // Group adicional entries (not in maestro rates) by recipe+cabecera so that
  // the same recipe added under different cabeceras renders as separate visual groups.
  const adicionalByRecipe = useMemo(() => {
    const map = new Map<string, { recipeName: string; rows: { key: string; draft: DetailDraft }[] }>();
    for (const [key, draft] of details) {
      if (draft.maestroId === maestro.maestro_id && !maestroRateRecipeIds.has(draft.recipeId)) {
        const groupKey = `${draft.recipeId}:${draft.cabeceraId ?? ''}`;
        const existing = map.get(groupKey);
        if (existing) {
          existing.rows.push({ key, draft });
        } else {
          map.set(groupKey, { recipeName: draft.recipeName, rows: [{ key, draft }] });
        }
      }
    }
    return map;
  }, [details, maestro.maestro_id, maestroRateRecipeIds]);

  // Map of recipeId → Set<cabeceraId|null> for adicionales already added by this maestro.
  // Used by AddAdicionalModal to block only the (recipe, cabecera) pairs already in use,
  // allowing the same recipe to be added again under a different cabecera.
  const adicionalUsedCabecerasMap = useMemo(() => {
    const map = new Map<string, Set<string | null>>();
    for (const [, draft] of details) {
      if (draft.maestroId === maestro.maestro_id && !maestroRateRecipeIds.has(draft.recipeId)) {
        if (!map.has(draft.recipeId)) map.set(draft.recipeId, new Set());
        map.get(draft.recipeId)!.add(draft.cabeceraId);
      }
    }
    return map;
  }, [details, maestro.maestro_id, maestroRateRecipeIds]);

  // Calculate subtotal. With cabeceras: all entries with cantidad > 0.
  // Without cabeceras: MDO sub-items only when checkbox-selected; flat APUs (no MDO) always included.
  const subtotal = useMemo(() => {
    let sum = 0;
    for (const [k, draft] of details) {
      if (!k.startsWith(`${maestro.maestro_id}:`)) continue;
      if (draft.cantidad <= 0) continue;
      const isFlatApu = draft.mdoMaterialId === null;
      if (hasCabeceras || selectedKeys.has(k) || isFlatApu) {
        sum += draft.cantidad * draft.precioUnitario;
      }
    }
    return sum;
  }, [maestro.maestro_id, details, selectedKeys, hasCabeceras]);

  const extrasCliente = extras.filter((e) => e.categoria === 'varios');
  const extrasMaestro = extras.filter((e) => e.categoria === 'varios_maestro');
  const totalDescuentos = descuentos.reduce((sum, d) => sum + d.monto, 0);
  const totalExtras = extras.reduce((sum, e) => sum + e.monto, 0);
  const neto = subtotal + totalExtras - totalDescuentos;

  // Check accumulated from existing details
  const accumulated = existingDetails
    .filter((d) => d.maestro_id === maestro.maestro_id)
    .reduce((sum, d) => sum + d.subtotal, 0);

  return (
    <Card className="overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between p-4 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <p className="text-sm font-semibold text-gray-800">{maestro.nombre}</p>
          {maestro.cedula && <p className="text-xs text-gray-400">{maestro.cedula}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-brand-dark">
            ${neto.toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Rates */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 pb-4 space-y-3 pt-3">
          {/* Column headers — only shown in flat (no-cabecera) mode */}
          {!hasCabeceras && (
            <div className="flex items-center gap-2 text-xs text-gray-400 pb-1 border-b border-gray-50">
              <div className="flex-1" />
              <div className="w-20 text-right">
                <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-[10px] font-medium">Precio</span>
              </div>
              <div className="w-20 text-center text-[10px] font-medium text-gray-400">Cantidad</div>
              <div className="w-20 text-right text-[10px] font-medium text-gray-400">Total</div>
            </div>
          )}
          {maestro.rates.map((rate) => {
            const hasMdo = rate.mdo_items && rate.mdo_items.length > 0;
            const noMdoProgress = progressMap.get(`${rate.recipe_id}:`);

            // ── Cabecera mode: hierarchical display (one block per APU, cabecera entries as sub-rows) ──
            if (hasCabeceras) {
              // Each APU block = one recipe (non-MDO) or one MDO item
              const apuBlocks = hasMdo
                ? rate.mdo_items.map((mdo) => ({
                    baseKey: `${maestro.maestro_id}:${rate.recipe_id}:${mdo.material_id}:`,
                    progressKey: `${rate.recipe_id}:${mdo.material_id}`,
                    label: mdo.material_nombre,
                    defaultPrecio: mdo.precio_unitario,
                    mdoMaterialId: mdo.material_id as string | null,
                    mdoMaterialNombre: mdo.material_nombre as string | null,
                  }))
                : [{
                    baseKey: `${maestro.maestro_id}:${rate.recipe_id}::`,
                    progressKey: `${rate.recipe_id}:`,
                    label: rate.recipe_nombre,
                    defaultPrecio: rate.precio_unitario,
                    mdoMaterialId: null as string | null,
                    mdoMaterialNombre: null as string | null,
                  }];

              return (
                <div key={rate.recipe_id} className="space-y-2 pb-2 border-b border-gray-50 last:border-0">
                  {hasMdo && (
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide pt-1">
                      {rate.recipe_nombre}
                    </p>
                  )}
                  {apuBlocks.map((block) => {
                    // All cabecera entries for this APU in the draft (keys start with baseKey)
                    const blockEntries = Array.from(details.entries()).filter(([k]) => k.startsWith(block.baseKey));
                    const blockTotal = blockEntries.reduce((s, [, d]) => s + d.cantidad * d.precioUnitario, 0);

                    // Available cabeceras: exclude already-assigned ones
                    const usedCabeceraIds = new Set(blockEntries.map(([k]) => k.slice(block.baseKey.length)));
                    const sinCabeceraUsed = usedCabeceraIds.has('');
                    const availableNamed = cabeceras.filter((c) => !usedCabeceraIds.has(c.id));

                    const isAddingThis = addingForKey === block.baseKey;

                    // Progress context
                    const perMdoProgress = progressMap.get(block.progressKey);
                    const progress = perMdoProgress ?? (noMdoProgress?.cantidad_registrada === 0 ? noMdoProgress : undefined);

                    return (
                      <div key={block.baseKey} className="space-y-1.5">
                        {/* APU name + total */}
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-700">{block.label}</p>
                          <p className="text-sm font-medium text-gray-700">${blockTotal.toFixed(2)}</p>
                        </div>
                        {progress && (
                          <p className="text-xs text-gray-400">
                            <span className={progress.cantidad_registrada >= progress.cantidad_ot ? 'text-green-600' : 'text-blue-500'}>
                              {progress.cantidad_registrada.toFixed(1)} / {progress.cantidad_ot.toFixed(1)} {progress.unidad} planillados
                            </span>
                          </p>
                        )}

                        {/* Cabecera assignment rows */}
                        {blockEntries.map(([entryKey, d]) => {
                          const cabId = entryKey.slice(block.baseKey.length);
                          const cabNombre = cabId
                            ? (cabeceras.find((c) => c.id === cabId)?.nombre ?? cabId)
                            : 'Sin cabecera';
                          const lineTotal = d.cantidad * d.precioUnitario;
                          return (
                            <div key={entryKey} className="flex items-center gap-1.5 pl-3 border-l-2 border-blue-100">
                              <span className="text-xs text-blue-700 font-medium min-w-0 flex-1 truncate" title={cabNombre}>
                                {cabNombre}
                              </span>
                              {isEditable && (
                                <input
                                  type="number" min={0} step="0.01"
                                  value={d.precioUnitario || ''}
                                  onChange={(e) => onUpdateDraft(entryKey, 'precioUnitario', Number(e.target.value) || 0)}
                                  className="w-16 px-1.5 py-1 text-xs border border-gray-200 rounded text-right bg-yellow-50"
                                  title="Precio unitario"
                                />
                              )}
                              {!isEditable && (
                                <span className="w-16 px-1.5 py-1 text-xs text-right text-gray-500 bg-yellow-50 rounded">
                                  ${d.precioUnitario.toFixed(2)}
                                </span>
                              )}
                              <input
                                type="number" min={0} step="any"
                                value={rawQuantities.get(entryKey) ?? (d.cantidad === 0 ? '' : String(d.cantidad))}
                                placeholder="0"
                                disabled={!isEditable}
                                onChange={(e) => { setRawQty(entryKey, e.target.value); onUpdateDraft(entryKey, 'cantidad', e.target.value === '' ? 0 : Number(e.target.value) || 0); }}
                                className="w-16 px-1.5 py-1 text-xs border border-gray-200 rounded text-center disabled:bg-gray-50"
                                title="Cantidad"
                              />
                              <span className="text-xs text-gray-500 w-16 text-right shrink-0">${lineTotal.toFixed(2)}</span>
                              {isEditable && (
                                <button
                                  type="button"
                                  onClick={() => onRemoveCabeceraEntry(entryKey)}
                                  className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded shrink-0"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}

                        {/* No entries yet */}
                        {blockEntries.length === 0 && !isAddingThis && (
                          <p className="text-xs text-gray-400 pl-3 italic">Sin cantidades asignadas</p>
                        )}

                        {/* Add cabecera entry form */}
                        {isEditable && (availableNamed.length > 0 || !sinCabeceraUsed) && (
                          <div className="pl-3">
                            {!isAddingThis ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setAddingForKey(block.baseKey);
                                  setNewCabeceraId('');
                                  setNewPrecio(String(block.defaultPrecio));
                                  setNewCantidad('');
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                Asignar a cabecera
                              </button>
                            ) : (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <select
                                  value={newCabeceraId}
                                  onChange={(e) => setNewCabeceraId(e.target.value)}
                                  className="text-xs border border-gray-200 rounded px-2 py-1 flex-1 min-w-0"
                                >
                                  <option value="">-- Cabecera --</option>
                                  {!sinCabeceraUsed && <option value="__sin__">Sin cabecera</option>}
                                  {availableNamed.map((c) => (
                                    <option key={c.id} value={c.id}>{c.nombre}</option>
                                  ))}
                                </select>
                                <input
                                  type="number" min={0} step="0.01"
                                  value={newPrecio}
                                  onChange={(e) => setNewPrecio(e.target.value)}
                                  className="w-16 text-xs border border-gray-200 rounded px-1.5 py-1 text-right bg-yellow-50"
                                  title="Precio unitario"
                                />
                                <input
                                  type="number" min={0} step="any"
                                  value={newCantidad}
                                  placeholder="Cant."
                                  onChange={(e) => setNewCantidad(e.target.value)}
                                  className="w-16 text-xs border border-gray-200 rounded px-1.5 py-1 text-center"
                                />
                                <button
                                  type="button"
                                  disabled={!newCabeceraId || !newCantidad || Number(newCantidad) <= 0}
                                  onClick={() => {
                                    const resolvedCabId = newCabeceraId === '__sin__' ? null : newCabeceraId;
                                    onAddCabeceraEntry(resolvedCabId, {
                                      maestroId: maestro.maestro_id,
                                      recipeId: rate.recipe_id,
                                      mdoMaterialId: block.mdoMaterialId,
                                      mdoMaterialNombre: block.mdoMaterialNombre,
                                      recipeName: rate.recipe_nombre,
                                      unidad: rate.unidad,
                                      precioUnitario: Number(newPrecio) || block.defaultPrecio,
                                      cantidad: Number(newCantidad) || 0,
                                      notas: '',
                                    });
                                    setAddingForKey(null);
                                  }}
                                  className="text-xs bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-40"
                                >
                                  Agregar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setAddingForKey(null)}
                                  className="text-xs text-gray-400 hover:text-gray-600 px-1"
                                >✕</button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            // ── Original mode (no cabeceras): flat row per APU ──
            const baseRows = hasMdo
              ? rate.mdo_items.map((mdo) => ({
                  key: `${maestro.maestro_id}:${rate.recipe_id}:${mdo.material_id}:`,
                  progressKey: `${rate.recipe_id}:${mdo.material_id}`,
                  label: mdo.material_nombre,
                  defaultPrice: mdo.precio_unitario,
                  isSubItem: true,
                }))
              : [{
                  key: `${maestro.maestro_id}:${rate.recipe_id}::`,
                  progressKey: `${rate.recipe_id}:`,
                  label: rate.recipe_nombre,
                  defaultPrice: rate.precio_unitario,
                  isSubItem: false,
                }];

            return (
              <div key={rate.recipe_id} className="space-y-1">
                {hasMdo && (
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide pt-1">
                    {rate.recipe_nombre}
                  </p>
                )}
                {baseRows.map((row) => {
                  const draft = details.get(row.key);
                  const isSelected = selectedKeys.has(row.key);
                  const cantidad = draft?.cantidad ?? 0;
                  const precio = draft?.precioUnitario ?? row.defaultPrice;
                  const lineTotal = (!row.isSubItem || isSelected) ? cantidad * precio : 0;
                  const notas = draft?.notas ?? '';
                  const perMdoProgress = progressMap.get(row.progressKey);
                  const progress = perMdoProgress
                    ?? (noMdoProgress?.cantidad_registrada === 0 ? noMdoProgress : undefined);

                  return (
                    <div key={row.key} className="space-y-1">
                      <div className="flex items-center gap-2">
                        {row.isSubItem && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!isEditable}
                            onChange={() => onToggleKey(row.key)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-500 cursor-pointer shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${row.isSubItem ? (isSelected ? 'text-gray-700' : 'text-gray-400') : 'text-gray-700'}`}>
                            {row.label}
                          </p>
                          {progress && (
                            <p className="text-xs text-gray-400">
                              <span className={`${progress.cantidad_registrada >= progress.cantidad_ot ? 'text-green-600' : 'text-blue-500'}`}>
                                {progress.cantidad_registrada.toFixed(1)} / {progress.cantidad_ot.toFixed(1)} {progress.unidad} planillados
                              </span>
                            </p>
                          )}
                        </div>
                        {(!row.isSubItem || isSelected) && (
                          <div className="w-20">
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              value={precio || ''}
                              disabled={!isEditable || (row.isSubItem && !isSelected)}
                              onChange={(e) => onUpdateDraft(row.key, 'precioUnitario', Number(e.target.value) || 0)}
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded text-right bg-yellow-50 disabled:bg-gray-50"
                              title="Precio unitario (editable)"
                            />
                          </div>
                        )}
                        {(!row.isSubItem || isSelected) && (
                          <div className="w-20">
                            <input
                              type="number"
                              min={0}
                              step="any"
                              value={rawQuantities.get(row.key) ?? (cantidad === 0 ? '' : String(cantidad))}
                              placeholder="0"
                              disabled={!isEditable || (row.isSubItem && !isSelected)}
                              onChange={(e) => { setRawQty(row.key, e.target.value); onUpdateDraft(row.key, 'cantidad', e.target.value === '' ? 0 : Number(e.target.value) || 0); }}
                              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded text-center disabled:bg-gray-50"
                              title="Cantidad"
                            />
                          </div>
                        )}
                        {(!row.isSubItem || isSelected) ? (
                          <p className="w-20 text-right text-sm font-medium text-gray-700 shrink-0">
                            ${lineTotal.toFixed(2)}
                          </p>
                        ) : (
                          <p className="w-20 text-right text-xs text-gray-300 shrink-0">—</p>
                        )}
                      </div>
                      {isEditable && isSelected && (
                        <div className={`${row.isSubItem ? 'pl-6' : ''}`}>
                          <input
                            type="text"
                            value={notas}
                            placeholder="Notas (opcional)"
                            onChange={(e) => onUpdateDraft(row.key, 'notas', e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-100 rounded text-gray-500 placeholder-gray-300"
                          />
                        </div>
                      )}
                      {!isEditable && notas && isSelected && (
                        <p className={`text-xs text-gray-400 italic ${row.isSubItem ? 'pl-6' : ''}`}>{notas}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Adicionales (APUs not in maestro rates) */}
          <div className="pt-2 border-t border-gray-100 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500 uppercase">Adicionales</p>
              {adicionalByRecipe.size > 0 && (
                <div className="flex items-center gap-2 text-[10px]">
                  <div className="w-20 text-right">
                    <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">Precio</span>
                  </div>
                  <div className="w-20 text-center font-medium text-gray-400">Cantidad</div>
                  <div className="w-20 text-right font-medium text-gray-400">Total</div>
                </div>
              )}
            </div>

            {adicionalByRecipe.size === 0 && (
              <p className="text-xs text-gray-400 italic">Sin adicionales</p>
            )}

            {Array.from(adicionalByRecipe.entries()).map(([groupKey, { recipeName, rows }]) => {
              const hasMdo = rows.some((r) => r.draft.mdoMaterialId !== null);
              const firstCabeceraId = rows[0]?.draft.cabeceraId ?? null;
              const cabeceraNombre = firstCabeceraId
                ? cabeceras.find((c) => c.id === firstCabeceraId)?.nombre ?? null
                : null;
              return (
                <div key={groupKey} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      {cabeceraNombre && (
                        <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-medium w-fit mb-0.5">
                          {cabeceraNombre}
                        </span>
                      )}
                      {hasMdo ? (
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide pt-1">
                          {recipeName}
                        </p>
                      ) : (
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide pt-1">
                          {recipeName}
                        </span>
                      )}
                    </div>
                    {isEditable && (
                      <button
                        type="button"
                        onClick={() => onDeleteAdicionales(rows.map((r) => r.key))}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Eliminar adicional"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {rows.map(({ key, draft }) => {
                    const isSelected = selectedKeys.has(key);
                    const cantidad = draft.cantidad;
                    const precio = draft.precioUnitario;
                    const lineTotal = (!hasMdo || isSelected) ? cantidad * precio : 0;
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center gap-2">
                          {hasMdo && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!isEditable}
                              onChange={() => onToggleKey(key)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-500 cursor-pointer shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${hasMdo ? (isSelected ? 'text-gray-700' : 'text-gray-400') : 'text-gray-700'}`}>
                              {hasMdo ? (draft.mdoMaterialNombre ?? recipeName) : recipeName}
                            </p>
                          </div>
                          {(!hasMdo || isSelected) && (
                            <div className="w-20">
                              <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={precio || ''}
                                disabled={!isEditable}
                                onChange={(e) => onUpdateDraft(key, 'precioUnitario', Number(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded text-right bg-yellow-50 disabled:bg-gray-50"
                                title="Precio unitario (obligatorio para adicionales)"
                              />
                            </div>
                          )}
                          {(!hasMdo || isSelected) && (
                            <div className="w-20">
                              <input
                                type="number"
                                min={0}
                                step="any"
                                value={rawQuantities.get(key) ?? (cantidad === 0 ? '' : String(cantidad))}
                                placeholder="0"
                                disabled={!isEditable}
                                onChange={(e) => { setRawQty(key, e.target.value); onUpdateDraft(key, 'cantidad', e.target.value === '' ? 0 : Number(e.target.value) || 0); }}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded text-center disabled:bg-gray-50"
                              />
                            </div>
                          )}
                          {(!hasMdo || isSelected) ? (
                            <p className="w-20 text-right text-sm font-medium text-gray-700 shrink-0">
                              ${lineTotal.toFixed(2)}
                            </p>
                          ) : (
                            <p className="w-20 text-right text-xs text-gray-300 shrink-0">—</p>
                          )}
                        </div>
                        {isEditable && (!hasMdo || isSelected) && (
                          <input
                            type="text"
                            value={draft.notas ?? ''}
                            placeholder="Notas (opcional)"
                            onChange={(e) => onUpdateDraft(key, 'notas', e.target.value)}
                            className={`w-full px-2 py-1 text-xs border border-gray-100 rounded text-gray-500 placeholder-gray-300 ${hasMdo ? 'pl-6' : ''}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {isEditable && (
              <div className="pt-1">
                <button
                  onClick={() => setShowAdicionalModal(true)}
                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Agregar adicional
                </button>
              </div>
            )}
          </div>

          {showAdicionalModal && (
            <AddAdicionalModal
              maestroId={maestro.maestro_id}
              rateRecipeIds={maestroRateRecipeIds}
              adicionalUsedCabecerasMap={adicionalUsedCabecerasMap}
              cabeceras={cabeceras}
              onConfirm={onAddAdicionales}
              onClose={() => setShowAdicionalModal(false)}
            />
          )}

          {/* Servicios Varios Cliente (se cargan al cliente con 35% utilidad) */}
          <div className="pt-2 border-t border-gray-100 space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase">Servicios Varios Cliente</p>
            {extrasCliente.map((e) => (
              <ServicioVariosRow key={e.id} extra={e} planillaId={planillaId} canDelete={isEditable} />
            ))}
            {extrasCliente.length === 0 && (
              <p className="text-xs text-gray-400 italic">Sin servicios varios cliente</p>
            )}
            {isEditable && (
              <div className="pt-1">
                {showServicioForm ? (
                  <AddServicioVariosForm
                    planillaId={planillaId}
                    maestroId={maestro.maestro_id}
                    categoria="varios"
                    cabeceras={cabeceras}
                    onClose={() => setShowServicioForm(false)}
                  />
                ) : (
                  <button
                    onClick={() => setShowServicioForm(true)}
                    className="text-xs text-green-600 hover:text-green-800 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Agregar servicio cliente
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Servicios Varios Maestro (solo para planilla del maestro y contabilidad, sin markup) */}
          <div className="pt-2 border-t border-gray-100 space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase">Servicios Varios Maestro</p>
            {extrasMaestro.map((e) => (
              <ServicioVariosRow key={e.id} extra={e} planillaId={planillaId} canDelete={isEditable} />
            ))}
            {extrasMaestro.length === 0 && (
              <p className="text-xs text-gray-400 italic">Sin servicios varios maestro</p>
            )}
            {isEditable && (
              <div className="pt-1">
                {showServicioMaestroForm ? (
                  <AddServicioVariosForm
                    planillaId={planillaId}
                    maestroId={maestro.maestro_id}
                    categoria="varios_maestro"
                    cabeceras={cabeceras}
                    onClose={() => setShowServicioMaestroForm(false)}
                  />
                ) : (
                  <button
                    onClick={() => setShowServicioMaestroForm(true)}
                    className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Agregar servicio maestro
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Multas (descuentos) */}
          <div className="pt-2 border-t border-gray-100 space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase">Multas / Descuentos</p>
            {descuentos.map((d) => (
              <MultaRow key={d.id} descuento={d} planillaId={planillaId} canDelete={isEditable} />
            ))}
            {descuentos.length === 0 && (
              <p className="text-xs text-gray-400 italic">Sin multas</p>
            )}
            {isEditable && (
              <div className="pt-1">
                {showMultaForm ? (
                  <AddMultaForm
                    planillaId={planillaId}
                    maestroId={maestro.maestro_id}
                    cabeceras={cabeceras}
                    onClose={() => setShowMultaForm(false)}
                  />
                ) : (
                  <button
                    onClick={() => setShowMultaForm(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" />
                    Agregar multa
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="pt-2 border-t border-gray-100 text-xs text-gray-500 space-y-1">
            <div className="flex justify-between">
              <span>Subtotal rubros</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>
            {extrasCliente.length > 0 && (
              <div className="flex justify-between text-green-600">
                <span>Srv. Varios Cliente</span>
                <span className="font-medium">+${extrasCliente.reduce((s, e) => s + e.monto, 0).toFixed(2)}</span>
              </div>
            )}
            {extrasMaestro.length > 0 && (
              <div className="flex justify-between text-teal-600">
                <span>Srv. Varios Maestro</span>
                <span className="font-medium">+${extrasMaestro.reduce((s, e) => s + e.monto, 0).toFixed(2)}</span>
              </div>
            )}
            {totalDescuentos > 0 && (
              <div className="flex justify-between text-red-600">
                <span>Descuentos</span>
                <span className="font-medium">-${totalDescuentos.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-brand-dark">
              <span>Neto</span>
              <span>${neto.toFixed(2)}</span>
            </div>
            {accumulated > 0 && (
              <div className="flex justify-between">
                <span>Acumulado anterior</span>
                <span>${accumulated.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── Multa Row ───────────────────────────────────────────────────────

function MultaRow({ descuento, planillaId, canDelete }: { descuento: DescuentoDTO; planillaId: string; canDelete: boolean }) {
  const deleteDescuento = useDeleteDescuento(planillaId);

  const handleDelete = async () => {
    try {
      await deleteDescuento.mutateAsync(descuento.id);
      toast('success', 'Multa eliminada');
    } catch {
      toast('error', 'Error al eliminar multa');
    }
  };

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        {descuento.cabecera_nombre && (
          <span className="inline-block text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded mb-0.5 font-medium">
            {descuento.cabecera_nombre}
          </span>
        )}
        <span className="block text-sm text-gray-600 truncate">{descuento.descripcion}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-medium text-red-600">-${descuento.monto.toFixed(2)}</span>
        {canDelete && (
          <button onClick={handleDelete} className="text-gray-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Add Multa Form ──────────────────────────────────────────────────

function AddMultaForm({
  planillaId,
  maestroId,
  cabeceras,
  onClose,
}: {
  planillaId: string;
  maestroId: string;
  cabeceras: CabeceraDTO[];
  onClose: () => void;
}) {
  const [descripcion, setDescripcion] = useState('');
  const [monto, setMonto] = useState('');
  const [cabeceraId, setCabeceraId] = useState<string>('');
  const createDescuento = useCreateDescuento(planillaId);

  const handleSubmit = async () => {
    if (!descripcion.trim() || !monto || Number(monto) <= 0) {
      toast('error', 'Complete descripción y monto');
      return;
    }
    try {
      await createDescuento.mutateAsync({
        maestroId,
        tipo: 'multa',
        cabeceraId: cabeceraId || null,
        descripcion: descripcion.trim(),
        monto: Number(monto),
      });
      toast('success', 'Multa agregada');
      onClose();
    } catch {
      toast('error', 'Error al agregar multa');
    }
  };

  return (
    <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
      {cabeceras.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Cabecera (opcional)</label>
          <select
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800 bg-white"
            value={cabeceraId}
            onChange={(e) => setCabeceraId(e.target.value)}
          >
            <option value="">Sin cabecera</option>
            {cabeceras.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <Input
        label="Descripción"
        placeholder="Ej: Multa por incumplimiento de horario"
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
      />
      <Input
        label="Monto"
        type="number"
        placeholder="0.00"
        value={monto}
        onChange={(e) => setMonto(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
        <Button size="sm" onClick={handleSubmit} isLoading={createDescuento.isPending}>Agregar</Button>
      </div>
    </div>
  );
}

// ── Servicio Varios Row ──────────────────────────────────────────────

function ServicioVariosRow({ extra, planillaId, canDelete }: { extra: ExtraDTO; planillaId: string; canDelete: boolean }) {
  const deleteExtra = useDeleteExtra(planillaId);

  const handleDelete = async () => {
    try {
      await deleteExtra.mutateAsync(extra.id);
      toast('success', 'Servicio eliminado');
    } catch {
      toast('error', 'Error al eliminar servicio');
    }
  };

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        {extra.cabecera_nombre && (
          <span className="inline-block text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded mb-0.5 font-medium">
            {extra.cabecera_nombre}
          </span>
        )}
        <span className="block text-sm text-gray-600 truncate">{extra.descripcion}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-medium text-green-600">+${extra.monto.toFixed(2)}</span>
        {canDelete && (
          <button onClick={handleDelete} className="text-gray-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Add Servicio Varios Form ─────────────────────────────────────────

function AddServicioVariosForm({
  planillaId,
  maestroId,
  categoria,
  cabeceras,
  onClose,
}: {
  planillaId: string;
  maestroId: string;
  categoria: 'varios' | 'varios_maestro';
  cabeceras: CabeceraDTO[];
  onClose: () => void;
}) {
  const [descripcion, setDescripcion] = useState('');
  const [monto, setMonto] = useState('');
  const [cabeceraId, setCabeceraId] = useState<string>('');
  const createExtra = useCreateExtra(planillaId);

  const handleSubmit = async () => {
    if (!descripcion.trim() || !monto || Number(monto) <= 0) {
      toast('error', 'Complete descripción y monto');
      return;
    }
    try {
      await createExtra.mutateAsync({
        maestroId,
        categoria,
        cabeceraId: cabeceraId || null,
        descripcion: descripcion.trim(),
        monto: Number(monto),
      });
      toast('success', 'Servicio agregado');
      onClose();
    } catch {
      toast('error', 'Error al agregar servicio');
    }
  };

  const bgClass = categoria === 'varios_maestro' ? 'bg-teal-50' : 'bg-green-50';

  return (
    <div className={`space-y-2 ${bgClass} p-3 rounded-lg`}>
      {cabeceras.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Cabecera (opcional)</label>
          <select
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800 bg-white"
            value={cabeceraId}
            onChange={(e) => setCabeceraId(e.target.value)}
          >
            <option value="">Sin cabecera</option>
            {cabeceras.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>
      )}
      <Input
        label="Descripción"
        placeholder="Ej: Traslado de materiales, trabajo adicional..."
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
      />
      <Input
        label="Monto"
        type="number"
        placeholder="0.00"
        value={monto}
        onChange={(e) => setMonto(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
        <Button size="sm" onClick={handleSubmit} isLoading={createExtra.isPending}>Agregar</Button>
      </div>
    </div>
  );
}

// ── Add Adicional Modal ──────────────────────────────────────────────

function AddAdicionalModal({
  maestroId,
  rateRecipeIds,
  adicionalUsedCabecerasMap,
  cabeceras,
  onConfirm,
  onClose,
}: {
  maestroId: string;
  rateRecipeIds: Set<string>;
  adicionalUsedCabecerasMap: Map<string, Set<string | null>>;
  cabeceras: CabeceraDTO[];
  onConfirm: (rows: DetailDraft[]) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeListDTO | null>(null);
  const [selectedCabeceraId, setSelectedCabeceraId] = useState<string>('');

  const { data: recipesData } = useRecipes({ search: search || undefined, isActive: true, limit: 30 });
  const { data: recipeDetail, isLoading: loadingDetail } = useRecipe(selectedRecipe?.id ?? '');

  const availableRecipes = (recipesData?.data ?? []).filter((r) => {
    if (rateRecipeIds.has(r.id)) return false;
    const cabeceraToCheck = selectedCabeceraId || null;
    const usedCabeceras = adicionalUsedCabecerasMap.get(r.id);
    if (usedCabeceras && usedCabeceras.has(cabeceraToCheck)) return false;
    return true;
  });

  const mdoMaterials = recipeDetail
    ? (recipeDetail.materials as unknown as RecipeMaterialWithMdo[]).filter((m) => m.is_mdo)
    : [];

  const handleConfirm = () => {
    if (!selectedRecipe || !recipeDetail) return;

    const cabeceraId = selectedCabeceraId || null;

    let newRows: DetailDraft[];
    if (mdoMaterials.length > 0) {
      newRows = mdoMaterials.map((m) => ({
        maestroId,
        recipeId: recipeDetail.id,
        mdoMaterialId: m.material_id,
        cabeceraId,
        recipeName: recipeDetail.nombre,
        mdoMaterialNombre: m.material_nombre,
        unidad: recipeDetail.unidad,
        precioUnitario: m.material_precio_unitario ?? 0,
        cantidad: 0,
        notas: '',
      }));
    } else {
      newRows = [{
        maestroId,
        recipeId: recipeDetail.id,
        mdoMaterialId: null,
        cabeceraId,
        recipeName: recipeDetail.nombre,
        mdoMaterialNombre: null,
        unidad: recipeDetail.unidad,
        precioUnitario: 0,
        cantidad: 0,
        notas: '',
      }];
    }

    onConfirm(newRows);
    onClose();
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Agregar Adicional" size="md">
      <div className="space-y-4">
        {cabeceras.length > 0 && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Cabecera (opcional)</label>
            <select
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800 bg-white"
              value={selectedCabeceraId}
              onChange={(e) => setSelectedCabeceraId(e.target.value)}
            >
              <option value="">Sin cabecera</option>
              {cabeceras.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
          </div>
        )}
        <Input
          label="Buscar APU"
          placeholder="Nombre o código del APU..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setSelectedRecipe(null); }}
        />

        <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
          {availableRecipes.length === 0 ? (
            <p className="p-3 text-sm text-gray-400 text-center">
              {search ? 'Sin resultados' : 'Escriba para buscar APUs'}
            </p>
          ) : (
            availableRecipes.map((recipe) => (
              <button
                key={recipe.id}
                onClick={() => setSelectedRecipe(recipe)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedRecipe?.id === recipe.id ? 'bg-purple-50 text-purple-700' : 'text-gray-700'}`}
              >
                <span className="font-medium">{recipe.codigo}</span>
                <span className="ml-2 text-gray-500">{recipe.nombre}</span>
              </button>
            ))
          )}
        </div>

        {selectedRecipe && (
          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            {loadingDetail ? (
              <p className="text-gray-400 text-xs">Cargando items de MDO...</p>
            ) : mdoMaterials.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Items de MDO que se agregarán:</p>
                <ul className="space-y-1">
                  {mdoMaterials.map((m) => (
                    <li key={m.material_id} className="text-sm text-gray-600 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                      {m.material_nombre}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-400 mt-2">Complete precio y cantidad en la planilla.</p>
              </div>
            ) : (
              <p className="text-gray-500">
                Sin items de MDO. Se agregará una fila para <span className="font-medium">{selectedRecipe.nombre}</span>.
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button
            disabled={!selectedRecipe || loadingDetail}
            onClick={handleConfirm}
          >
            Agregar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Planilla Progress Panel ──────────────────────────────────────────

function PlanillaProgressPanel({ projectId }: { projectId: string }) {
  const { data: progress, isLoading } = usePlanillaProgress(projectId);

  if (isLoading || !progress || progress.length === 0) return null;

  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Avance de planillas por rubro</h3>
      <div className="space-y-2">
        {progress.map((item) => (
          <div key={`${item.recipe_id}:${item.mdo_material_id ?? ''}`} className="text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="text-gray-600 truncate">
                {item.mdo_material_nombre
                  ? `${item.recipe_nombre} — ${item.mdo_material_nombre}`
                  : item.recipe_nombre}
              </span>
              <span className="text-gray-500 whitespace-nowrap ml-2">
                {item.cantidad_registrada.toFixed(1)} / {item.cantidad_ot.toFixed(1)} {item.unidad}
              </span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  item.porcentaje >= 100 ? 'bg-green-500' : item.porcentaje >= 50 ? 'bg-blue-500' : 'bg-orange-400'
                }`}
                style={{ width: `${Math.min(item.porcentaje, 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
